import {
  alignReceiptLine,
  stripReceiptMarkup,
  visibleReceiptLength,
  wrapReceiptLine,
  wrapReceiptText,
} from "@/lib/receiptMarkup";

export interface ReceiptPreviewItem {
  name: string;
  quantity: number;
  unitPrice: number;
  vatRatePercent: number;
}

export type ReceiptSeparatorStyle = "double" | "single" | "dots";
export type ReceiptTextAlignment = "left" | "center" | "right";
export type ReceiptFontSize = "small" | "normal" | "large";
export type ReceiptFontFamily = "standard" | "condensed" | "arial";

export interface ReceiptPreviewOptions {
  headerText: string;
  footerText: string;
  logoConfigured: boolean;
  includeCardPayment: boolean;
  charactersPerLine?: number;
  separatorStyle?: ReceiptSeparatorStyle;
  headerAlign?: ReceiptTextAlignment;
  footerAlign?: ReceiptTextAlignment;
  titleText?: string;
  fontSize?: ReceiptFontSize;
  fontFamily?: ReceiptFontFamily;
  now?: Date;
}

export interface ReceiptPreviewLine {
  text: string;
  align: ReceiptTextAlignment;
  kind: "header" | "body" | "footer" | "separator" | "title" | "tse" | "emphasis";
}

export interface ReceiptPreviewModel {
  width: number;
  separator: string;
  dashLine: string;
  lines: ReceiptPreviewLine[];
  logoConfigured: boolean;
  fontSize: ReceiptFontSize;
  fontFamily: ReceiptFontFamily;
}

export const RECEIPT_PREVIEW_ITEMS: ReceiptPreviewItem[] = [
  { name: "Strompauschale", quantity: 1, unitPrice: 3.5, vatRatePercent: 19 },
  { name: "Brötchen", quantity: 2, unitPrice: 1.2, vatRatePercent: 7 },
  { name: "Eis am Stiel", quantity: 1, unitPrice: 2.5, vatRatePercent: 19 },
];

export const RECEIPT_TEST_RECEIPT_NUMBER = "TEST-2026-000042";
export const RECEIPT_TEST_PAYMENT_METHOD = "Bar";
export const RECEIPT_TEST_CASHIER = "Muster, Max";
export const RECEIPT_TEST_TSE_SERIAL = "STUB-TSE-0001";
export const RECEIPT_TEST_TSE_COUNTER = 42;
export const RECEIPT_TEST_TSE_ALGORITHM = "ecdsa-plain-SHA256";
export const RECEIPT_TEST_TSE_QR_PLACEHOLDER = "[TSE QR-Code]";

export const DEFAULT_RECEIPT_CHARACTERS_PER_LINE = 32;
export const MIN_RECEIPT_CHARACTERS_PER_LINE = 24;
export const MAX_RECEIPT_CHARACTERS_PER_LINE = 48;
export const DEFAULT_RECEIPT_SEPARATOR_STYLE: ReceiptSeparatorStyle = "double";
export const DEFAULT_RECEIPT_HEADER_ALIGN: ReceiptTextAlignment = "center";
export const DEFAULT_RECEIPT_FOOTER_ALIGN: ReceiptTextAlignment = "left";
export const DEFAULT_RECEIPT_TITLE_TEXT = "TESTBELEG";
export const DEFAULT_RECEIPT_FONT_SIZE: ReceiptFontSize = "normal";
export const DEFAULT_RECEIPT_FONT_FAMILY: ReceiptFontFamily = "arial";

export function normalizeReceiptCharactersPerLine(value?: number | null): number {
  if (
    value != null &&
    value >= MIN_RECEIPT_CHARACTERS_PER_LINE &&
    value <= MAX_RECEIPT_CHARACTERS_PER_LINE
  ) {
    return value;
  }
  return DEFAULT_RECEIPT_CHARACTERS_PER_LINE;
}

export function normalizeReceiptSeparatorStyle(
  value?: ReceiptSeparatorStyle | string | null,
): ReceiptSeparatorStyle {
  if (value === "single" || value === "dots") {
    return value;
  }
  return DEFAULT_RECEIPT_SEPARATOR_STYLE;
}

export function normalizeReceiptAlignment(
  value: ReceiptTextAlignment | string | null | undefined,
  fallback: ReceiptTextAlignment,
): ReceiptTextAlignment {
  if (value === "left" || value === "center" || value === "right") {
    return value;
  }
  return fallback;
}

export function normalizeReceiptFontSize(
  value?: ReceiptFontSize | string | null,
): ReceiptFontSize {
  if (value === "small" || value === "large") {
    return value;
  }
  return DEFAULT_RECEIPT_FONT_SIZE;
}

export function normalizeReceiptFontFamily(
  value?: ReceiptFontFamily | string | null,
): ReceiptFontFamily {
  if (value === "condensed") {
    return value;
  }
  if (value === "standard" || value === "arial") {
    return "arial";
  }
  return DEFAULT_RECEIPT_FONT_FAMILY;
}

export function receiptPreviewFontSizePx(fontSize: ReceiptFontSize): number {
  switch (fontSize) {
    case "small":
      return 10;
    case "large":
      return 13;
    default:
      return 11;
  }
}

export function receiptPreviewFontFamilyCss(fontFamily: ReceiptFontFamily): string {
  if (fontFamily === "condensed") {
    return '"Lucida Console", "Courier New", Courier, monospace';
  }
  return 'Arial, "Helvetica Neue", Helvetica, sans-serif';
}

export function buildReceiptSeparator(width: number, style: ReceiptSeparatorStyle): string {
  switch (style) {
    case "single":
      return "-".repeat(width);
    case "dots":
      return "·".repeat(width);
    default:
      return "=".repeat(width);
  }
}

function formatEuro(amount: number): string {
  return amount.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPercent(rate: number): string {
  return `${rate.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`;
}

function padLine(left: string, right: string, width: number): string {
  const leftVisible = stripReceiptMarkup(left);
  const rightVisible = stripReceiptMarkup(right);
  const gap = width - leftVisible.length - rightVisible.length;
  if (gap >= 1) {
    return left + " ".repeat(gap) + right;
  }
  return `${left.slice(0, Math.max(0, left.length - 1))} ${right}`;
}

function appendWrappedSection(
  lines: ReceiptPreviewLine[],
  text: string,
  width: number,
  align: ReceiptTextAlignment,
  kind: ReceiptPreviewLine["kind"],
): void {
  for (const rawLine of text.split("\n")) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      continue;
    }
    for (const wrapped of wrapReceiptLine(trimmed, width)) {
      lines.push({
        text: alignReceiptLine(wrapped, width, align),
        align,
        kind,
      });
    }
  }
}

function computeReceiptTotalGross(): number {
  return RECEIPT_PREVIEW_ITEMS.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
}

function buildVatBreakdown(): Array<{ rate: number; net: number; vat: number; gross: number }> {
  const groups = new Map<number, number>();
  for (const item of RECEIPT_PREVIEW_ITEMS) {
    const gross = item.quantity * item.unitPrice;
    groups.set(item.vatRatePercent, (groups.get(item.vatRatePercent) ?? 0) + gross);
  }

  return [...groups.entries()]
    .sort(([left], [right]) => right - left)
    .map(([rate, gross]) => {
      const net = Math.round((gross / (1 + rate / 100)) * 100) / 100;
      const vat = Math.round((gross - net) * 100) / 100;
      return { rate, net, vat, gross };
    });
}

export function buildTseTestSignatureSync(timestamp: Date, totalGross: number): string {
  const payload = [
    "campdesk-test",
    RECEIPT_TEST_RECEIPT_NUMBER,
    totalGross.toFixed(2),
    RECEIPT_TEST_PAYMENT_METHOD,
    RECEIPT_TEST_TSE_COUNTER,
    timestamp.toISOString(),
  ].join("|");

  let hash = 0;
  for (let index = 0; index < payload.length; index += 1) {
    hash = (hash * 31 + payload.charCodeAt(index)) >>> 0;
  }
  const digest = hash.toString(16).padStart(8, "0").toUpperCase();
  return `STUB:${RECEIPT_TEST_TSE_COUNTER}:${digest}${digest}${digest}${digest}`.slice(0, 42);
}

function buildDummyCardPaymentLines(now: Date, dashLine: string, width: number): ReceiptPreviewLine[] {
  const date = now.toLocaleDateString("de-DE");
  const time = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const total = computeReceiptTotalGross();
  const rows = [
    "",
    "KARTENZAHLUNG",
    dashLine,
    "Referenz: TEST-000001",
    `Betrag:   ${formatEuro(total)} EUR`,
    "Karte:    MasterCard",
    "PAN:      ************1234",
    "Beleg-Nr: 42",
    "Trace-Nr: 123456",
    "Genehm.:  OK1234",
    "VU-Nr:    12345678",
    "Terminal: TERM-001",
    `Datum:    ${date}`,
    `Uhrzeit:  ${time}`,
    dashLine,
    "Zahlung erfolgreich",
  ];

  return rows.flatMap((row) =>
    wrapReceiptText(row, width).map((text) => ({
      text,
      align: "left" as const,
      kind: "body" as const,
    })),
  );
}

function buildTseLines(now: Date, dashLine: string, width: number, totalGross: number): ReceiptPreviewLine[] {
  const tseStart = new Date(now.getTime() - 1000);
  const signature = buildTseTestSignatureSync(now, totalGross);
  const rows = [
    "",
    "**TSE / Kassensicherung**",
    dashLine,
    `TSE-Seriennr.: ${RECEIPT_TEST_TSE_SERIAL}`,
    `TSE-Transakt.: ${RECEIPT_TEST_TSE_COUNTER}`,
    `Signaturzähler: ${RECEIPT_TEST_TSE_COUNTER}`,
    `Start: ${tseStart.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })}`,
    `Ende:  ${now.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })}`,
    `Algorithmus: ${RECEIPT_TEST_TSE_ALGORITHM}`,
    "Signatur:",
    signature,
    RECEIPT_TEST_TSE_QR_PLACEHOLDER,
    dashLine,
    "TSE-Testdaten (Stub) – nicht rechtsgültig",
  ];

  return rows.flatMap((row) =>
    wrapReceiptText(row, width).map((text) => ({
      text,
      align: "left" as const,
      kind: "tse" as const,
    })),
  );
}

export function buildReceiptPreviewModel(options: ReceiptPreviewOptions): ReceiptPreviewModel {
  const width = normalizeReceiptCharactersPerLine(options.charactersPerLine);
  const separatorStyle = normalizeReceiptSeparatorStyle(options.separatorStyle);
  const headerAlign = normalizeReceiptAlignment(options.headerAlign, DEFAULT_RECEIPT_HEADER_ALIGN);
  const footerAlign = normalizeReceiptAlignment(options.footerAlign, DEFAULT_RECEIPT_FOOTER_ALIGN);
  const fontSize = normalizeReceiptFontSize(options.fontSize);
  const fontFamily = normalizeReceiptFontFamily(options.fontFamily);
  const separator = buildReceiptSeparator(width, separatorStyle);
  const dashLine = "-".repeat(width);
  const now = options.now ?? new Date();
  const lines: ReceiptPreviewLine[] = [{ text: separator, align: "left", kind: "separator" }];

  const header = options.headerText.trim();
  if (header) {
    appendWrappedSection(lines, header, width, headerAlign, "header");
  } else {
    for (const row of ["**Campingplatz Sonne**", "Musterstraße 1", "12345 Musterstadt"]) {
      lines.push({
        text: alignReceiptLine(row, width, headerAlign),
        align: headerAlign,
        kind: "header",
      });
    }
  }

  const title = (options.titleText?.trim() || DEFAULT_RECEIPT_TITLE_TEXT).toUpperCase();
  for (const wrapped of wrapReceiptLine(title, width)) {
    lines.push({
      text: alignReceiptLine(wrapped, width, "center"),
      align: "center",
      kind: "title",
    });
  }

  lines.push({ text: separator, align: "left", kind: "separator" });
  lines.push({
    text: padLine("Artikel", "Betrag", width),
    align: "left",
    kind: "body",
  });
  lines.push({ text: dashLine, align: "left", kind: "separator" });

  for (const item of RECEIPT_PREVIEW_ITEMS) {
    const lineTotal = item.quantity * item.unitPrice;
    const qtyLabel = item.quantity > 1 ? `${item.quantity}x ` : "";
    const itemLine = padLine(`${qtyLabel}${item.name}`, formatEuro(lineTotal), width);
    for (const wrapped of wrapReceiptLine(itemLine, width)) {
      lines.push({ text: wrapped, align: "left", kind: "body" });
    }

    if (item.quantity > 1) {
      const unitLine = padLine(
        `  ${item.quantity}x ${formatEuro(item.unitPrice)}`,
        `${formatPercent(item.vatRatePercent)} MwSt`,
        width,
      );
      for (const wrapped of wrapReceiptLine(unitLine, width)) {
        lines.push({ text: wrapped, align: "left", kind: "body" });
      }
    }
  }

  const total = computeReceiptTotalGross();
  lines.push({ text: dashLine, align: "left", kind: "separator" });
  lines.push({
    text: padLine("**Summe**", `**${formatEuro(total)} EUR**`, width),
    align: "left",
    kind: "body",
  });

  appendWrappedSection(lines, "MwSt-Aufstellung", width, "left", "body");
  for (const entry of buildVatBreakdown()) {
    const vatLine = padLine(
      `${formatPercent(entry.rate)} Netto ${formatEuro(entry.net)}`,
      `MwSt ${formatEuro(entry.vat)}`,
      width,
    );
    for (const wrapped of wrapReceiptLine(vatLine, width)) {
      lines.push({ text: wrapped, align: "left", kind: "body" });
    }
    const grossLine = padLine(`  Brutto ${formatPercent(entry.rate)}`, formatEuro(entry.gross), width);
    for (const wrapped of wrapReceiptLine(grossLine, width)) {
      lines.push({ text: wrapped, align: "left", kind: "body" });
    }
  }

  lines.push({ text: dashLine, align: "left", kind: "separator" });
  lines.push({
    text: padLine("Gesamt inkl. MwSt", `${formatEuro(total)} EUR`, width),
    align: "left",
    kind: "body",
  });

  lines.push({ text: "", align: "left", kind: "body" });
  for (const row of [
    `Beleg-Nr.:   ${RECEIPT_TEST_RECEIPT_NUMBER}`,
    `Zahlungsart: ${RECEIPT_TEST_PAYMENT_METHOD}`,
    `Datum/Zeit:  ${now.toLocaleString("de-DE")}`,
    `Kassierer:   ${RECEIPT_TEST_CASHIER}`,
  ]) {
    for (const wrapped of wrapReceiptText(row, width)) {
      lines.push({ text: wrapped, align: "left", kind: "body" });
    }
  }

  lines.push(...buildTseLines(now, dashLine, width, total));

  if (options.includeCardPayment) {
    lines.push(...buildDummyCardPaymentLines(now, dashLine, width));
  }

  const footer = options.footerText.trim();
  if (footer) {
    lines.push({ text: "", align: footerAlign, kind: "footer" });
    appendWrappedSection(lines, footer, width, footerAlign, "footer");
  } else {
    lines.push({ text: "", align: footerAlign, kind: "footer" });
    for (const row of [
      "USt-IdNr.: DE123456789",
      "Steuernr.: 123/456/78901",
      "*Vielen Dank für Ihren Besuch!*",
    ]) {
      appendWrappedSection(lines, row, width, footerAlign, "footer");
    }
  }

  lines.push({ text: separator, align: "left", kind: "separator" });

  return {
    width,
    separator,
    dashLine,
    lines,
    logoConfigured: options.logoConfigured,
    fontSize,
    fontFamily,
  };
}

export function buildReceiptPreviewText(options: ReceiptPreviewOptions): string {
  const model = buildReceiptPreviewModel(options);
  const output: string[] = [];

  for (const line of model.lines) {
    if (line.kind === "header" && model.logoConfigured && output.length > 0) {
      const headerLines = model.lines.filter((entry) => entry.kind === "header");
      const isLastHeader = headerLines[headerLines.length - 1] === line;
      output.push(stripReceiptMarkup(line.text));
      if (isLastHeader) {
        output.push(alignReceiptLine("[Logo]", model.width, "center"));
      }
      continue;
    }
    output.push(stripReceiptMarkup(line.text));
  }

  return output.join("\n");
}

export function receiptLineUsesFullWidth(line: string, width: number): boolean {
  return visibleReceiptLength(line) >= width;
}
